import { A, useLocation } from "@solidjs/router";
import { Match, Show, Suspense, Switch, createEffect, createMemo, createSignal, onCleanup, onMount } from "solid-js";
import { isServer } from "solid-js/web";
import { ErrorBoundary } from "~/components/error";
import {
  AppUIContextProvider,
  ExtendedPaneMode,
  NavigationPaneMode,
  isExtendedPaneVisible,
  useAppUI,
} from "~/components/layouts/minimal/AppUIContext";
import { InviteShareModal } from "~/components/projects/InviteShareModal";
import { ProjectSwitcherModal } from "~/components/projects/ProjectSwitcherModal";
import { PromptBarWithStatus, type PromptSubmission } from "~/components/prompt";
import { useGateActionByAuthenticated } from "~/hooks/useGateActionByAuthenticated";
import { useLanguageRegion } from "~/hooks/useLanguageRegion";
import { useI18n } from "~/i18n/context";
import { useWire } from "~/wire";
import { type ChatPageMessages, chatPageDictionary } from "./ChatStreamPage.i18n";

import { cookieStorage, makePersisted } from "@solid-primitives/storage";
import { stAnalytics } from "@storytell/analytics";
import { subscribeToGlobalEvent } from "@storytell/events";
import { proseMirrorToRichText } from "@storytell/rich-text";
import { ChatStatus, UIStatus } from "@storytell/streaming";
import { StButton } from "@storytell/ui/buttons";
import { TbLayoutSidebarRightExpand, TbUsers, TbUsersPlus } from "solid-icons/tb";
import { twMerge } from "tailwind-merge";
import { ChatAccessDeniedModal } from "~/components/chat/ChatAccessDeniedModal";
import { ChatRenderer } from "~/components/chat/messages/ChatRenderer";
import { useGateActionByAsssetsProcessing } from "~/hooks/useGateActionByAssetsProcessing";
import { useGateActionByChatPermission } from "~/hooks/useGateActionByChatPermission";
import { gateCreditsDescriptions, useGateActionByCredits } from "~/hooks/useGateActionByCredits";
import { scrollToAnchor } from "~/utils/headingAnchorUtils";
import { useMessageHasKnowledgeSearchAssets } from "~/utils/messageHasKnowledgeSearch";
import { ChatHomepage } from "./ChatHomepage";
import images from "./images.json";

// Speed for playback, 1.0 is normal speed
// TODO(amalucelli): Increased by 1.25x from 5.0 to 6.25
const DEFAULT_PLAYBACK_SPEED = 6.25;

const ChatStreamPageContent = () => {
  const { state, actions } = useAppUI();
  const wire = useWire();
  const location = useLocation();

  // Detect if we're on the homepage
  const isHomepage = createMemo(() => !wire.chat.shouldShowContent());

  // Auto-set navigation pane mode based on route
  createEffect(() => {
    actions.setNavigationPaneModeForRoute(isHomepage());
  });
  const languageRegion = useLanguageRegion();
  const { currentLanguage, t } = useI18n();

  // Signal to force title update when event is received
  const [titleUpdateTrigger, setTitleUpdateTrigger] = createSignal(0);

  // Active project info for banner
  const activeProjectId = () => wire.userContext.get()?.projectId;
  const activeProject = createMemo(() => (activeProjectId() ? wire.projects.get(activeProjectId()!) : undefined));

  // Invite modal state
  const [inviteModalOpen, setInviteModalOpen] = createSignal(false);

  // Projects modal state
  const [isProjectModalOpen, setIsProjectModalOpen] = createSignal(false);
  const openProjectModal = useGateActionByAuthenticated(
    () => ({
      title: "Sign up to switch projects",
      description: "Projects are a great way to collaborate with your team.",
    }),
    () => setIsProjectModalOpen(true),
  );

  const chatTitle = createMemo(() => {
    // Include trigger in dependencies to force re-evaluation
    titleUpdateTrigger();

    const chatId = wire.chat.getChatId();
    if (!chatId) return "New Chat";
    // Always show a title when chatId exists, fallback to "New Chat" if title hasn't generated yet
    const title = wire.chatTitle.getChatTitle(chatId);
    return title || "New Chat";
  });

  // Listen for title generation events
  onMount(() => {
    const unsubscribe = subscribeToGlobalEvent("chat-title-generated", (event) => {
      // Force memo to re-evaluate by updating trigger
      setTitleUpdateTrigger((prev) => prev + 1);
    });

    onCleanup(() => unsubscribe());
  });

  // Type-safe translation helper for this page
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const translate = (key: keyof ChatPageMessages, params?: Record<string, any>) => {
    return t(key as string, chatPageDictionary[currentLanguage()] as unknown as Record<string, string>, params);
  };

  // Handle stopping streaming
  const handleStopStreaming = async () => {
    try {
      await wire.chat.interruptStream();
    } catch (error) {
      console.error("Failed to stop streaming:", error);
      // TODO(amalucelli): Add proper error handling/notification to user
    }
  };

  // Create streaming state for the prompt bar
  const streamingState = {
    status: () => (wire.chat.isChatStreaming() ? ChatStatus.GENERATING : ChatStatus.DONE),
    uiStatus: () => (wire.chat.isChatStreaming() ? UIStatus.GENERATING : UIStatus.READY),
    accumulatedMetrics: () => ({
      ingressTokens: 0,
      egressTokens: 0,
      ingressBytes: 0,
      egressBytes: 0,
      lastUpdated: new Date(),
    }),
    error: undefined,
  };

  // Handle prompt submission
  const handlePromptSubmit = useGateActionByChatPermission(
    useGateActionByAsssetsProcessing(
      useGateActionByCredits(
        () => gateCreditsDescriptions.prompt,
        (submission: PromptSubmission) => {
          actions.submitPrompt();

          const workspaceId = wire.auth.realm().workspaceId;
          const projectId = wire.userContext.get()?.projectId!;

          // Start the chat stream with the user's prompt, language region, and scope
          const scope = wire.chat.scope.getScopeForPromptSubmit();
          const locale = languageRegion();
          const submissionContent = submission.richText;

          try {
            stAnalytics.track("prompt_submitted", {
              prompt: submission.text,
              organizationId: wire.auth.realm().organizationId,
              tenantId: wire.auth.realm().tenantId,
              projectId: wire.userContext.get()?.projectId!,
              threadId: wire.chat.getChatId() ?? "",
              threadPromptCount: wire.chat.getChatMessages()?.length ?? 0,
            });
          } catch (error) {
            console.error(error);
          }

          wire.chat.startOrContinueChat({
            submission: submissionContent,
            locale,
            projectId,
            workspaceId,
            scope,
            model: submission.model,
          });
        },
      ),
    ),
  );

  onMount(() => {
    const unsubscribe = subscribeToGlobalEvent("submit-prompt", (ev) => {
      const richText = proseMirrorToRichText(ev.detail.prompt);
      handlePromptSubmit({
        richText,
        text: ev.detail.textPrompt,
      });
    });
    onCleanup(() => {
      unsubscribe();
    });
  });

  // TODO: @andi streaming Add playback

  const initial = 22; //3;
  const [imageIndex, setImageIndex] = makePersisted(createSignal(initial), {
    storage: cookieStorage,
    name: "picv1",
    storageOptions: {
      path: "/",
    },
  });
  const nextImage = () => {
    if (imageIndex() < images.length - 1) {
      setImageIndex(imageIndex() + 1);
    } else {
      setImageIndex(0);
    }
  };
  const previousImage = () => {
    if (imageIndex() > 0) {
      setImageIndex(imageIndex() - 1);
    } else {
      setImageIndex(images.length - 1);
    }
  };

  onMount(() => {
    window.addEventListener("keydown", (e) => {
      if (e.key === "ArrowRight" && e.metaKey && e.ctrlKey) {
        nextImage();
      } else if (e.key === "ArrowLeft" && e.metaKey && e.ctrlKey) {
        previousImage();
      }
    });
  });

  // Handle scroll to anchor on page load and hash changes
  onMount(() => {
    const handleAnchorScroll = () => {
      const hash = location.hash;
      if (!hash || hash.length <= 1) return;

      const anchorId = hash.substring(1); // Remove the # prefix

      // Wait for content to render before scrolling
      // Use a small delay to ensure DOM is ready
      setTimeout(() => {
        scrollToAnchor(anchorId, true);
      }, 300);
    };

    // Scroll on initial load if hash exists
    handleAnchorScroll();

    // Listen for hash changes during the session
    const handleHashChange = () => {
      handleAnchorScroll();
    };

    window.addEventListener("hashchange", handleHashChange);

    onCleanup(() => {
      window.removeEventListener("hashchange", handleHashChange);
    });
  });

  const hasConceptsCheck = useMessageHasKnowledgeSearchAssets();
  const hasConcepts = createMemo(() => {
    const messages = wire.chat.getChatMessages();
    return !!messages?.findLast((m) => hasConceptsCheck(m));
  });

  createEffect(() => {
    if (!state.extendedPane.activeArtifact) {
      if (hasConcepts() && !wire.ui.conceptsPoweringChat.closed()) {
        actions.setExtendedPaneConceptsPowering(true);
      } else {
        actions.setExtendedPaneMode(ExtendedPaneMode.HIDDEN);
        actions.setExtendedPaneConceptsPowering(false);
      }
    }
  });

  // Track if component is mounted (client-side only)
  const [isMounted, setIsMounted] = createSignal(false);
  onMount(() => {
    setIsMounted(true);
  });

  // Check for access denied errors (client-side only to avoid hydration issues)
  createEffect(() => {
    if (!isMounted() || isServer) return; // Guard against server-side execution and pre-mount
    
    const error = wire.chat.getChatError();
    if (error) {
      // Handle different error formats
      let errorMessage = "";
      let errorCode: string | number | undefined = undefined;
      
      if (error instanceof Error) {
        errorMessage = error.message || error.toString();
        // Check if the error has an errorResult property (from ErrorResult)
        const errorWithResult = error as Error & { errorResult?: { code?: string | number; message?: string; detail?: string } };
        if (errorWithResult.errorResult) {
          errorCode = errorWithResult.errorResult.code;
          errorMessage = errorWithResult.errorResult.message || errorWithResult.errorResult.detail || errorMessage;
        }
      } else if (typeof error === "string") {
        errorMessage = error;
      } else if (error && typeof error === "object") {
        // Handle ErrorResult-like objects
        if ("message" in error) {
          errorMessage = String(error.message);
        }
        if ("code" in error) {
          errorCode = error.code as string | number;
        }
        if ("detail" in error && !errorMessage) {
          errorMessage = String(error.detail);
        }
      } else {
        errorMessage = String(error);
      }
      
      const errorStr = errorMessage.toLowerCase();
      const codeStr = errorCode ? String(errorCode).toLowerCase() : "";
      
      // Log the actual values for debugging (log each property separately for better visibility)
      console.log("=== Chat Error Detection ===");
      console.log("errorMessage:", errorMessage);
      console.log("errorCode:", errorCode);
      console.log("errorStr:", errorStr);
      console.log("codeStr:", codeStr);
      console.log("errorType:", typeof error);
      if (error instanceof Error) {
        console.log("error.message:", error.message);
        const errorWithResult = error as Error & { errorResult?: { code?: string | number; message?: string; detail?: string } };
        if (errorWithResult.errorResult) {
          console.log("errorResult.code:", errorWithResult.errorResult.code);
          console.log("errorResult.message:", errorWithResult.errorResult.message);
          console.log("errorResult.detail:", errorWithResult.errorResult.detail);
        }
      }
      console.log("============================");
      
      const isAccessDenied =
        errorStr.includes("access denied") ||
        errorStr.includes("unauthorized") ||
        errorStr.includes("forbidden") ||
        errorStr.includes("not found or user is unauthorized") ||
        errorStr.includes("permission denied") ||
        errorStr.includes("not found") ||
        errorStr.includes("does not exist") ||
        errorStr.includes("can not get chat") ||
        codeStr === "403" ||
        codeStr === "401" ||
        codeStr === "404" ||
        codeStr === "500" || // 500 might also indicate access issues in some cases
        codeStr === "2" || // gRPC error code 2 (NOT_FOUND)
        codeStr.includes("permission_denied") ||
        codeStr.includes("not_found") ||
        codeStr.includes("unauthorized");
      
      console.log("Access denied check result:", { isAccessDenied, errorMessage, errorCode });
      
      if (isAccessDenied) {
        console.log("Opening access denied modal");
        wire.ui.chatAccessDenied.openModal();
        console.log("Modal state after openModal:", wire.ui.chatAccessDenied.isModalOpen());
      }
    } else {
      // Clear modal if error is cleared
      if (wire.ui.chatAccessDenied.isModalOpen()) {
        wire.ui.chatAccessDenied.closeModal();
      }
    }
  });

  const isAccessDeniedModalOpen = () => isMounted() && !isServer && wire.ui.chatAccessDenied.isModalOpen();

  return (
    <Suspense fallback={<LoadingPlayback />}>
      <div class="relative h-screen flex flex-col bg-canvas overflow-hidden">
        {/* Background Image - always present but fades based on state */}
        <div
          class={twMerge(
            "fixed z-0 inset-0 bg-cover bg-center bg-no-repeat pointer-events-none transition-opacity duration-1000 opacity-20 dark:opacity-30",
            wire.chat.shouldShowContent() && "opacity-0 dark:opacity-0",
          )}
          style={{
            "background-image": `url(/images/bg/${images[imageIndex()][2]})`,
          }}
        />
        {/* footer - only visible in initial state */}
        <div
          class="fixed z-10 bottom-0 left-0 right-0 text-sm bg-inset text-muted-text px-3 py-2 rounded-lg backdrop-blur-sm transition-opacity duration-1000"
          style={{
            opacity: wire.chat.shouldShowContent() ? "0" : "1",
          }}
          classList={{
            "pointer-events-none": wire.chat.shouldShowContent(),
          }}
        >
          <div class="flex flex-col items-center text-center">
            <p class="mb-0">
              <span class="hidden sm:inline">
                Enjoy this image of{" "}
                <a href={images[imageIndex()][1]} class="underline hover:text-brand" target="_blank" rel="noreferrer">
                  {images[imageIndex()][0]}
                </a>
                <span class="inline-block border border-gray-200 dark:border-gray-700 rounded-md px-1.5 py-0.5 ml-2 text-xs font-mono bg-gray-50 dark:bg-gray-800/50">
                  CMD+CTRL →
                </span>{" "}
                for next image |
              </span>{" "}
              {translate("agreement_prefix")}{" "}
              <A href="https://go.Storytell.ai/TOS" class="underline hover:text-brand">
                {translate("terms")}
              </A>{" "}
              {translate("and")}{" "}
              <A href="https://go.Storytell.ai/privacy" class="underline hover:text-brand">
                {translate("privacy_policy")}
              </A>
              .
            </p>
          </div>
        </div>

        {/* Project Banner - visible only when not on the homepage */}
        <Show when={wire.chat.shouldShowContent()}>
          <div class="flex-shrink-0 bg-brand py-0.5 text-on-brand">
            <div class="flex items-center justify-between px-6">
              <button type="button" onClick={openProjectModal} class="text-sm truncate text-on-brand cursor-pointer">
                Project: <span class="font-bold">{activeProject()?.name || ""}</span>
              </button>
              <StButton
                class="text-sm py-1 text-on-brand"
                noColor
                noBorder
                icon={TbUsersPlus}
                onClick={() => setInviteModalOpen(true)}
              >
                Invite others
              </StButton>
            </div>
          </div>
        </Show>

        {/* Chat Title Header - Always visible when there's a chat or streaming */}
        <Show when={wire.chat.getChatId() || wire.chat.isChatStreaming()}>
          <div
            class="flex-shrink-0 bg-canvas/95 backdrop-blur-sm border-b border-primary-border py-3"
            classList={{
              "pl-6 pr-24": hasConcepts(),
              "px-6": !hasConcepts(),
            }}
          >
            <div class="max-w-4xl mx-auto">
              {/* Mobile/tablet/small laptop layout - chat title below logo */}
              <div class="[@media(max-width:1279px)]:block [@media(min-width:1280px)]:hidden">
                <div class="pt-16">
                  <h1 class="text-lg font-semibold text-primary-text">{chatTitle()}</h1>
                </div>
              </div>
              {/* Large desktop layout - chat title in normal position */}
              <div class="[@media(max-width:1279px)]:hidden [@media(min-width:1280px)]:block">
                <h1 class="text-lg font-semibold text-primary-text">{chatTitle()}</h1>
              </div>
            </div>
          </div>
        </Show>

        <Switch>
          <Match when={!wire.chat.shouldShowContent()}>
            <ChatHomepage onSubmit={handlePromptSubmit} />
          </Match>
          <Match when={wire.chat.shouldShowContent()}>
            <main class="flex-1 flex flex-col overflow-hidden">
              {/* Scrollable content area */}
              <div
                class="flex-1 overflow-y-auto"
                classList={{ "blur-md": isAccessDeniedModalOpen() }}
              >
                <div class="max-w-4xl mx-auto px-4 py-6">
                  <ErrorBoundary>
                    <Suspense fallback={<LoadingPlayback />}>
                      <Show when={wire.chat.getChatId()} fallback={<LoadingPlayback />}>
                        {(chatId) => <ChatRenderer chatId={chatId()} />}
                      </Show>
                    </Suspense>
                  </ErrorBoundary>
                </div>

                <Show when={hasConcepts() && state.extendedPane.mode === ExtendedPaneMode.HIDDEN}>
                  <div class="fixed top-10 right-1">
                    <StButton
                      aria-label="Expand Section Concepts Powering this Chat"
                      icon={TbLayoutSidebarRightExpand}
                      onClick={() => {
                        actions.setExtendedPaneConceptsPowering(true);
                        wire.ui.conceptsPoweringChat.setClosed(false);
                      }}
                    >
                      Concepts
                    </StButton>
                  </div>
                </Show>
              </div>

              {/* Prompt bar at bottom */}
              <div
                class="flex-shrink-0 bg-canvas/95 backdrop-blur-sm"
                classList={{ "blur-md": isAccessDeniedModalOpen() }}
              >
                <div class="max-w-4xl mx-auto px-4 py-4">
                  <PromptBarWithStatus
                    onSubmit={handlePromptSubmit}
                    streamingState={streamingState}
                    onStopStreaming={handleStopStreaming}
                  />
                </div>
              </div>
            </main>
          </Match>
        </Switch>

        {/* Invite Share Modal */}
        <Show when={activeProjectId()}>
          {(projectId) => (
            <InviteShareModal
              isOpen={inviteModalOpen()}
              onClose={() => setInviteModalOpen(false)}
              projectId={projectId()}
            />
          )}
        </Show>

        {/* Project Switcher Modal */}
        <ProjectSwitcherModal open={isProjectModalOpen()} onOpenChange={setIsProjectModalOpen} />

        {/* Chat Access Denied Modal */}
        <ChatAccessDeniedModal />
      </div>
    </Suspense>
  );
};

export const ChatStreamPage = () => {
  return (
    <AppUIContextProvider>
      <ChatStreamPageContent />
    </AppUIContextProvider>
  );
};

const LoadingPlayback = () => {
  return (
    <div data-testid="loading-playback-state" class="flex-1 flex flex-col items-center justify-center relative z-10">
      <div class="max-w-md mx-auto px-6">
        <div class="rounded-3xl border border-primary-border backdrop-blur-xl bg-tile p-12 text-center">
          <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-brand mx-auto mb-6" />
          <p class="text-xl text-primary-text font-medium mb-2">Loading...</p>
          <p class="text-sm text-secondary-text">Preparing your conversation</p>
        </div>
      </div>
    </div>
  );
};
